import { cn } from '@/components/ui/utils';
import { ChevronRight, MessageCircle, Phone, Settings2 } from 'lucide-react';
import type { Agent, Skill } from '../types/cowork';

import { avatarSrc } from '../avatarSrc';

const CATEGORY_LABEL: Record<string, string> = {
  seo: 'SEO',
  'paid-media': 'Paid Media',
  content: 'Content',
  influencer: 'Influencer',
  strategy: 'Strategy',
  research: 'Analytics',
};

interface AgentCardProps {
  agent: Agent;
  skills: Skill[];
  onClick: (agent: Agent) => void;
  onChat?: (agent: Agent) => void;
  onCall?: (agent: Agent) => void;
  onEdit?: (agent: Agent) => void;
  compact?: boolean;
}

export function AgentCard({ agent, skills, onClick, onChat, onCall, onEdit, compact = false }: AgentCardProps) {
  const categories = [...new Set(skills.map(s => s.category))];

  if (compact) {
    return (
      <button
        onClick={() => onClick(agent)}
        className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#f8f5ff] transition-all group border border-transparent hover:border-[#be43ff]/15"
      >
        {avatarSrc(agent) ? (
          <img src={avatarSrc(agent)} alt={agent.name} className="w-8 h-8 rounded-xl object-cover shrink-0" />
        ) : (
          <div
            className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0 text-base"
            style={{ background: `linear-gradient(135deg, ${agent.gradientFrom}, ${agent.gradientTo})` }}
          >
            {agent.avatarEmoji}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-[12px] text-[#101828]" style={{ fontWeight: 600 }}>
            {agent.name}
          </div>
          <div className="text-[11px] text-[#98a2b3] truncate">{agent.title}</div>
        </div>
        <ChevronRight className="w-3.5 h-3.5 text-[#c4c9d4] group-hover:text-[#be43ff] transition-colors" />
      </button>
    );
  }

  return (
    <div
      className={cn(
        'group text-left w-full rounded-[20px] border border-gray-100 bg-white p-5',
        'hover:border-[#be43ff]/20 hover:shadow-[0_4px_16px_rgba(0,0,0,0.06)]',
        'transition-all duration-200 relative overflow-hidden flex flex-col'
      )}
    >
      {/* Gradient accent top */}
      <div
        className="absolute top-0 left-0 right-0 h-0.5 opacity-60"
        style={{ background: `linear-gradient(to right, ${agent.gradientFrom}, ${agent.gradientTo})` }}
      />

      {/* Clickable main area */}
      <button className="text-left flex-1" onClick={() => onClick(agent)}>
        {/* Avatar */}
        <div className="flex items-start justify-between mb-4">
          {avatarSrc(agent) ? (
            <img src={avatarSrc(agent)} alt={agent.name} className="w-12 h-12 rounded-2xl object-cover shadow-sm" />
          ) : (
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl shadow-sm"
              style={{
                background: `linear-gradient(135deg, ${agent.gradientFrom}22, ${agent.gradientTo}22)`,
                border: `1.5px solid ${agent.accentColor}30`,
              }}
            >
              {agent.avatarEmoji}
            </div>
          )}
          <div className="flex items-center gap-1">
            {agent.personalityTraits.slice(0, 1).map(trait => (
              <span
                key={trait}
                className="text-[10px] px-2 py-0.5 rounded-full"
                style={{ background: `${agent.accentColor}15`, color: agent.accentColor, fontWeight: 600 }}
              >
                {trait}
              </span>
            ))}
          </div>
        </div>

        {/* Name + title */}
        <div className="mb-1">
          <span className="text-[16px] text-[#101828]" style={{ fontWeight: 700 }}>
            {agent.name}
          </span>
          <span className="text-[13px] text-[#667085] ml-2" style={{ fontWeight: 400 }}>
            {agent.title}
          </span>
        </div>

        {/* Tagline */}
        <p className="text-[12px] text-[#98a2b3] mb-4" style={{ lineHeight: '1.5' }}>
          {agent.tagline}
        </p>
      </button>

      {/* Footer: skills + chat button */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-50 mt-1">
        <div className="flex flex-wrap gap-1">
          {categories.map(cat => (
            <span
              key={cat}
              className="text-[10px] px-2 py-0.5 rounded-full bg-[#f9fafb] text-[#667085]"
              style={{ fontWeight: 500 }}
            >
              {CATEGORY_LABEL[cat] ?? cat}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {onEdit && (
            <button
              onClick={e => { e.stopPropagation(); onEdit(agent); }}
              className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-lg text-[#98a2b3] hover:text-[#667085] hover:bg-gray-50 transition-all"
              style={{ fontWeight: 500 }}
            >
              <Settings2 className="w-3 h-3" />
            </button>
          )}
          {onCall && (
            <button
              onClick={e => { e.stopPropagation(); onCall(agent); }}
              className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-lg transition-all hover:opacity-90"
              style={{ background: `${agent.accentColor}15`, color: agent.accentColor, fontWeight: 600 }}
            >
              <Phone className="w-3 h-3" />
              Call
            </button>
          )}
          {onChat && (
            <button
              onClick={e => { e.stopPropagation(); onChat(agent); }}
              className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-lg transition-all hover:opacity-90"
              style={{ background: `${agent.accentColor}15`, color: agent.accentColor, fontWeight: 600 }}
            >
              <MessageCircle className="w-3 h-3" />
              Chat
            </button>
          )}
          <button
            onClick={() => onClick(agent)}
            className="text-[11px] text-[#c4c9d4] group-hover:text-[#be43ff] transition-colors flex items-center gap-1"
          >
            {skills.length} skill{skills.length !== 1 ? 's' : ''}
            <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}
